import React, { useMemo, useState } from "react";
import { Plus, Trash2, BookOpen, ListPlus, X } from "lucide-react";

import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./ui/command";

import { getDrugLibrary, blankMedication, calcDose } from "../data/prescriptions";
import { toast } from "sonner";

const ROUTE_OPTIONS = ["PO", "IV", "SC", "IM", "OTM", "Topical", "OU", "OD", "OS", "IV CRI"];
const FREQ_OPTIONS = ["SID", "BID", "TID", "QID", "EOD", "Weekly", "Monthly", "PRN", "Once"];

/**
 * Inline Rx editor. Renders on-screen only (wrapped in .no-print) so the
 * printed Rx pad shows the final medications list, not the editor.
 */
export default function RxEditor({
  medications,
  onChange,
  supportiveCare,
  onCareChange,
  diagnostics,
  onDiagnosticsChange,
  differentials,
  onDifferentialsChange,
  weight,
}) {
  const [libOpen, setLibOpen] = useState(false);

  const update = (i, patch) => {
    const next = medications.map((m, idx) => (idx === i ? { ...m, ...patch } : m));
    onChange(next);
  };

  const addBlank = () => {
    onChange([...medications, blankMedication()]);
  };

  const addFromLibrary = (libDrug) => {
    onChange([...medications, { ...libDrug }]);
    setLibOpen(false);
    toast.success(`Added ${libDrug.drug}`);
  };

  const remove = (i) => {
    onChange(medications.filter((_, idx) => idx !== i));
  };

  const updateCare = (i, val) => {
    onCareChange(supportiveCare.map((c, idx) => (idx === i ? val : c)));
  };
  const addCare = () => onCareChange([...(supportiveCare || []), ""]);
  const removeCare = (i) => onCareChange(supportiveCare.filter((_, idx) => idx !== i));

  return (
    <div
      data-testid="rx-editor"
      className="no-print rounded-xl border border-[#E2E8F0] bg-white p-5 shadow-sm"
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2">
            <ListPlus className="w-4 h-4 text-[#0F4C3A]" />
            <div
              className="text-sm tracking-[0.12em] uppercase"
              style={{ fontFamily: "'Work Sans', sans-serif", fontWeight: 600 }}
            >
              Edit Prescription
            </div>
            <Badge variant="outline" className="border-[#E2E8F0] text-[10px] font-normal">
              {medications.length} drug{medications.length === 1 ? "" : "s"}
            </Badge>
          </div>
          <p className="text-[12px] text-[#5C6B66] mt-1">
            Fine-tune the auto-generated Rx. Add drugs from the built-in library or free-form.
            Changes flow into Print and PDF instantly.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <LibraryPicker
            open={libOpen}
            setOpen={setLibOpen}
            onPick={addFromLibrary}
            excludeDrugs={medications.map((m) => m.drug.toLowerCase())}
          />
          <Button
            data-testid="add-blank-drug"
            variant="outline"
            onClick={addBlank}
            className="rounded-md border-[#E2E8F0] hover:border-[#0F4C3A] hover:text-[#0F4C3A]"
          >
            <Plus className="w-4 h-4 mr-1.5" /> Add row
          </Button>
        </div>
      </div>

      <div className="space-y-3">
        {medications.map((m, i) => (
          <div
            key={i}
            data-testid={`edit-row-${i}`}
            className="grid grid-cols-12 gap-2 rounded-md border border-[#E2E8F0] p-3 bg-[#F9F8F6]/60"
          >
            <div className="col-span-12 md:col-span-4">
              <MicroLabel>Drug</MicroLabel>
              <Input
                data-testid={`edit-drug-${i}`}
                value={m.drug}
                onChange={(e) => update(i, { drug: e.target.value })}
                placeholder="Drug name"
                className="border-[#E2E8F0] focus-visible:ring-[#0F4C3A]"
              />
            </div>
            <div className="col-span-6 md:col-span-2">
              <MicroLabel>Dose (mg/kg)</MicroLabel>
              <Input
                data-testid={`edit-mgkg-${i}`}
                type="number"
                step="0.01"
                value={m.doseMgPerKg ?? ""}
                onChange={(e) => {
                  const v = e.target.value;
                  const num = v === "" ? null : Number(v);
                  const auto = num != null ? `${num} mg/kg` : m.doseText;
                  update(i, { doseMgPerKg: num, doseText: num != null ? auto : m.doseText });
                }}
                placeholder="—"
                className="border-[#E2E8F0] focus-visible:ring-[#0F4C3A]"
              />
              {m.doseMgPerKg != null && weight ? (
                <div className="text-[10px] mt-1 text-[#0F4C3A] font-medium">
                  = {calcDose(m.doseMgPerKg, Number(weight))} total
                </div>
              ) : null}
            </div>
            <div className="col-span-6 md:col-span-2">
              <MicroLabel>Dose label</MicroLabel>
              <Input
                data-testid={`edit-dosetext-${i}`}
                value={m.doseText || ""}
                onChange={(e) => update(i, { doseText: e.target.value })}
                placeholder="e.g. 1 mg/kg"
                className="border-[#E2E8F0] focus-visible:ring-[#0F4C3A]"
              />
            </div>
            <div className="col-span-4 md:col-span-1">
              <MicroLabel>Route</MicroLabel>
              <Select value={m.route || ""} onValueChange={(v) => update(i, { route: v })}>
                <SelectTrigger data-testid={`edit-route-${i}`} className="border-[#E2E8F0]">
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent>
                  {ROUTE_OPTIONS.map((r) => (
                    <SelectItem key={r} value={r}>
                      {r}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-4 md:col-span-1">
              <MicroLabel>Freq.</MicroLabel>
              <Select value={m.frequency || ""} onValueChange={(v) => update(i, { frequency: v })}>
                <SelectTrigger data-testid={`edit-freq-${i}`} className="border-[#E2E8F0]">
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent>
                  {FREQ_OPTIONS.map((f) => (
                    <SelectItem key={f} value={f}>
                      {f}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-4 md:col-span-2">
              <MicroLabel>Duration</MicroLabel>
              <Input
                data-testid={`edit-duration-${i}`}
                value={m.duration || ""}
                onChange={(e) => update(i, { duration: e.target.value })}
                placeholder="e.g. 7 days"
                className="border-[#E2E8F0] focus-visible:ring-[#0F4C3A]"
              />
            </div>
            <div className="col-span-11">
              <MicroLabel>Notes (optional)</MicroLabel>
              <Input
                data-testid={`edit-notes-${i}`}
                value={m.notes || ""}
                onChange={(e) => update(i, { notes: e.target.value })}
                placeholder="Give with food, monitor renal function, etc."
                className="border-[#E2E8F0] focus-visible:ring-[#0F4C3A]"
              />
            </div>
            <div className="col-span-1 flex items-end justify-end">
              <button
                type="button"
                data-testid={`edit-delete-${i}`}
                onClick={() => remove(i)}
                aria-label="Delete drug"
                className="h-9 w-9 grid place-items-center rounded-md border border-[#E2E8F0] text-[#5C6B66] hover:text-[#E53E3E] hover:border-[#E53E3E]/60 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}

        {medications.length === 0 && (
          <div className="text-center text-[13px] text-[#5C6B66] py-6 border border-dashed border-[#E2E8F0] rounded-md">
            No drugs yet. Add one from the library or a blank row.
          </div>
        )}
      </div>

      {/* Care instructions editor */}
      <div className="mt-6 pt-5 border-t border-[#E2E8F0]">
        <div className="flex items-center justify-between mb-2">
          <div
            className="text-sm tracking-[0.12em] uppercase"
            style={{ fontFamily: "'Work Sans', sans-serif", fontWeight: 600 }}
          >
            Diagnostics
          </div>
          <Button
            data-testid="add-diagnostic"
            variant="outline"
            size="sm"
            onClick={() =>
              onDiagnosticsChange([...(diagnostics || []), { test: "", finding: "" }])
            }
            className="rounded-md border-[#E2E8F0] hover:border-[#0F4C3A] hover:text-[#0F4C3A]"
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> Add test
          </Button>
        </div>
        <div className="space-y-2">
          {(diagnostics || []).map((t, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-start">
              <Input
                data-testid={`edit-dx-test-${i}`}
                value={t.test}
                onChange={(e) =>
                  onDiagnosticsChange(
                    diagnostics.map((row, idx) => (idx === i ? { ...row, test: e.target.value } : row))
                  )
                }
                placeholder="Test (e.g. CBC, ultrasound, PCR)"
                className="col-span-4 border-[#E2E8F0] focus-visible:ring-[#0F4C3A]"
              />
              <Input
                data-testid={`edit-dx-finding-${i}`}
                value={t.finding}
                onChange={(e) =>
                  onDiagnosticsChange(
                    diagnostics.map((row, idx) => (idx === i ? { ...row, finding: e.target.value } : row))
                  )
                }
                placeholder="Expected finding / interpretation"
                className="col-span-7 border-[#E2E8F0] focus-visible:ring-[#0F4C3A]"
              />
              <button
                type="button"
                data-testid={`edit-dx-delete-${i}`}
                onClick={() =>
                  onDiagnosticsChange(diagnostics.filter((_, idx) => idx !== i))
                }
                aria-label="Delete diagnostic"
                className="col-span-1 h-9 w-9 grid place-items-center rounded-md border border-[#E2E8F0] text-[#5C6B66] hover:text-[#E53E3E] hover:border-[#E53E3E]/60 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
          {(!diagnostics || diagnostics.length === 0) && (
            <div className="text-center text-[12px] text-[#5C6B66] py-3 border border-dashed border-[#E2E8F0] rounded-md">
              No diagnostics listed.
            </div>
          )}
        </div>
      </div>

      {/* Differentials editor */}
      <div className="mt-6 pt-5 border-t border-[#E2E8F0]">
        <div className="flex items-center justify-between mb-2">
          <div
            className="text-sm tracking-[0.12em] uppercase"
            style={{ fontFamily: "'Work Sans', sans-serif", fontWeight: 600 }}
          >
            Differentials to Rule Out
          </div>
          <Button
            data-testid="add-differential"
            variant="outline"
            size="sm"
            onClick={() => onDifferentialsChange([...(differentials || []), ""])}
            className="rounded-md border-[#E2E8F0] hover:border-[#0F4C3A] hover:text-[#0F4C3A]"
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> Add DDx
          </Button>
        </div>
        <div className="space-y-2">
          {(differentials || []).map((d, i) => (
            <div key={i} className="flex gap-2 items-start">
              <span className="text-[#5C6B66] text-xs mt-2.5 w-4 text-right">{i + 1}.</span>
              <Input
                data-testid={`edit-ddx-${i}`}
                value={d}
                onChange={(e) =>
                  onDifferentialsChange(
                    differentials.map((row, idx) => (idx === i ? e.target.value : row))
                  )
                }
                placeholder="e.g. Pancreatitis / IBD / neoplasia"
                className="border-[#E2E8F0] focus-visible:ring-[#0F4C3A]"
              />
              <button
                type="button"
                data-testid={`edit-ddx-delete-${i}`}
                onClick={() =>
                  onDifferentialsChange(differentials.filter((_, idx) => idx !== i))
                }
                aria-label="Delete differential"
                className="h-9 w-9 shrink-0 grid place-items-center rounded-md border border-[#E2E8F0] text-[#5C6B66] hover:text-[#E53E3E] hover:border-[#E53E3E]/60 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
          {(!differentials || differentials.length === 0) && (
            <div className="text-center text-[12px] text-[#5C6B66] py-3 border border-dashed border-[#E2E8F0] rounded-md">
              No differentials listed.
            </div>
          )}
        </div>
      </div>

      {/* Care instructions editor */}
      <div className="mt-6 pt-5 border-t border-[#E2E8F0]">
        <div className="flex items-center justify-between mb-2">
          <div
            className="text-sm tracking-[0.12em] uppercase"
            style={{ fontFamily: "'Work Sans', sans-serif", fontWeight: 600 }}
          >
            Care Instructions
          </div>
          <Button
            data-testid="add-care"
            variant="outline"
            size="sm"
            onClick={addCare}
            className="rounded-md border-[#E2E8F0] hover:border-[#0F4C3A] hover:text-[#0F4C3A]"
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> Add note
          </Button>
        </div>
        <div className="space-y-2">
          {(supportiveCare || []).map((c, i) => (
            <div key={i} className="flex gap-2 items-start">
              <span className="text-[#5C6B66] text-xs mt-2.5 w-4 text-right">{i + 1}.</span>
              <Input
                data-testid={`edit-care-${i}`}
                value={c}
                onChange={(e) => updateCare(i, e.target.value)}
                placeholder="Care / follow-up instruction"
                className="border-[#E2E8F0] focus-visible:ring-[#0F4C3A]"
              />
              <button
                type="button"
                data-testid={`edit-care-delete-${i}`}
                onClick={() => removeCare(i)}
                aria-label="Delete note"
                className="h-9 w-9 shrink-0 grid place-items-center rounded-md border border-[#E2E8F0] text-[#5C6B66] hover:text-[#E53E3E] hover:border-[#E53E3E]/60 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
          {(!supportiveCare || supportiveCare.length === 0) && (
            <div className="text-center text-[12px] text-[#5C6B66] py-3 border border-dashed border-[#E2E8F0] rounded-md">
              No care instructions.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MicroLabel({ children }) {
  return (
    <Label className="text-[10px] uppercase tracking-[0.1em] text-[#5C6B66] font-semibold block mb-1">
      {children}
    </Label>
  );
}

function LibraryPicker({ open, setOpen, onPick, excludeDrugs }) {
  const library = useMemo(() => getDrugLibrary(), [open]);
  const available = useMemo(
    () => library.filter((d) => !excludeDrugs.includes(d.drug.toLowerCase())),
    [library, excludeDrugs]
  );
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          data-testid="open-drug-library"
          variant="outline"
          className="rounded-md border-[#0F4C3A]/40 text-[#0F4C3A] hover:bg-[#0F4C3A] hover:text-white"
        >
          <BookOpen className="w-4 h-4 mr-1.5" /> Drug library
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="p-0 w-[360px]">
        <Command>
          <CommandInput placeholder={`Search ${library.length} drugs…`} data-testid="library-search" />
          <CommandList>
            <CommandEmpty>No drug matched.</CommandEmpty>
            <CommandGroup>
              {available.map((d) => (
                <CommandItem
                  key={d.drug}
                  value={d.drug}
                  onSelect={() => onPick(d)}
                  data-testid={`library-item-${d.drug.replace(/\s+/g, "-").toLowerCase()}`}
                  className="flex flex-col items-start gap-0.5"
                >
                  <span className="font-medium text-[13px]">{d.drug}</span>
                  <span className="text-[11px] text-[#5C6B66]">
                    {d.doseText || "custom dose"} · {d.route} · {d.frequency}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
